/**
 * The browser-side mirror of Automations::MessageTemplate: a message with its
 * conditions understood.
 *
 *   {first_name} welcome to IUPAT. <if{employer}='TEST EMPLOYER',"Your dues are deducted automatically","You need to pay dues directly to District Council 1">
 *
 * The grammar is documented in full on the Ruby class, which renders every real
 * text. This file is written as the same scanner, line for line, and
 * test/integration/message_renderer_parity_test.rb runs it in Node against the
 * same cases — renders, lengths and error sentences — so the preview can never
 * say something the send will not. Change one, change both.
 *
 * Indexing is by code point (Array.from), matching Ruby's String#chars, so a
 * length and a position mean the same thing on both sides.
 *
 * Deliberately free of imports: the parity test runs this file as written.
 */

const SINGLE_QUOTES = ["'", "‘", "’"]
const DOUBLE_QUOTES = ['"', "“", "”"]
const WHITESPACE = [" ", "\t", "\r", "\n", "\f", "\v"]
const NAME_CHAR = /^[a-z0-9_]$/i
const NESTED = /<if\{/i
const TOKEN = /\{([a-z0-9_]+)\}/gi

/** Matches Automations::MessageTemplate::MAX_RAW_LENGTH. */
export const MAX_RAW_MESSAGE_LENGTH = 1000

/** Matches Automations::MessageTemplate::MAX_PREVIEW_CONDITIONS. */
export const MAX_PREVIEW_CONDITIONS = 3

export type ConditionOperator = "=" | "!="

export type MessageCondition = {
  index: number
  field: string
  operator: ConditionOperator
  value: string
  thenText: string
  elseText: string
  /** "<if{employer}" — how error sentences point at it. */
  excerpt: string
}

type Segment = string | MessageCondition

export type ParsedMessage = {
  source: string
  valid: boolean
  error: string | null
  conditions: MessageCondition[]
  segments: Segment[]
}

/** Which outcome to show for a condition, by index. true = the matching wording. */
export type ForcedOutcomes = Record<number, boolean>

type Values = Record<string, string | null | undefined>

/** The comparison rule, in one place: trimmed, lowercased, null as ''. */
export function normalizeConditionValue(value: unknown): string {
  return String(value ?? "")
    .replace(/^[ \t\r\n\f\v]+|[ \t\r\n\f\v]+$/g, "")
    .toLowerCase()
}

export function parseMessageTemplate(text: string): ParsedMessage {
  const source = text ?? ""
  const chars = Array.from(source)
  const segments: Segment[] = []
  const conditions: MessageCondition[] = []
  let error: string | null = null

  const fail = (message: string): null => {
    error = message
    return null
  }

  const isQuote = (char: string | undefined) =>
    char !== undefined && (SINGLE_QUOTES.includes(char) || DOUBLE_QUOTES.includes(char))

  const skipWhitespace = (j: number) => {
    while (j < chars.length && WHITESPACE.includes(chars[j])) j += 1
    return j
  }

  const opensConditionAt = (i: number) =>
    chars[i] === "<" &&
    chars[i + 1]?.toLowerCase() === "i" &&
    chars[i + 2]?.toLowerCase() === "f" &&
    chars[i + 3] === "{"

  // [text, index after the closing glyph], or null when never closed.
  const readQuoted = (j: number): [string, number] | null => {
    const family = SINGLE_QUOTES.includes(chars[j]) ? SINGLE_QUOTES : DOUBLE_QUOTES
    let k = j + 1
    let out = ""

    while (k < chars.length) {
      const char = chars[k]

      if (family.includes(char)) {
        if (k + 1 < chars.length && family.includes(chars[k + 1])) {
          out += char
          k += 2
          continue
        }

        return [out, k + 1]
      }

      out += char
      k += 1
    }

    return null
  }

  const nestedMessage = (prefix: string) =>
    `${prefix} has another condition inside it. Conditions can't go inside each other.`

  // The index after the closing >, or null having set the error.
  const readCondition = (start: number): number | null => {
    let j = start + 4
    let name = ""
    while (j < chars.length && NAME_CHAR.test(chars[j])) {
      name += chars[j]
      j += 1
    }

    if (name === "" || chars[j] !== "}") {
      return fail(
        `The condition starting at ‘${chars.slice(start, j).join("")}’ needs a field name in braces ` +
          "right after <if, like <if{employer}.",
      )
    }

    j += 1
    const excerpt = chars.slice(start, j).join("")
    const prefix = `The condition starting at ‘${excerpt}’`

    j = skipWhitespace(j)
    let operator: ConditionOperator
    if (chars[j] === "=") {
      operator = "="
      j += 1
    } else if (chars[j] === "!" && chars[j + 1] === "=") {
      operator = "!="
      j += 2
    } else {
      return fail(`${prefix} needs = or != after {${name}}.`)
    }

    j = skipWhitespace(j)
    if (!isQuote(chars[j])) {
      return fail(`${prefix} needs the value to compare in quotes after ${operator}, like 'TEST EMPLOYER'.`)
    }

    const value = readQuoted(j)
    if (value === null) return fail(`${prefix} has a quote that is never closed.`)

    j = skipWhitespace(value[1])
    const thenMissing = `${prefix} needs the wording to use when it matches, in quotes, after a comma.`
    if (chars[j] !== ",") return fail(thenMissing)

    j = skipWhitespace(j + 1)
    if (!isQuote(chars[j])) return fail(thenMissing)

    const thenText = readQuoted(j)
    if (thenText === null) return fail(`${prefix} has a quote that is never closed.`)
    if (NESTED.test(thenText[0])) return fail(nestedMessage(prefix))

    j = skipWhitespace(thenText[1])
    let elseText = ""

    if (chars[j] === ",") {
      j = skipWhitespace(j + 1)
      if (!isQuote(chars[j])) {
        return fail(`${prefix} needs the otherwise wording in quotes after the second comma.`)
      }

      const otherwise = readQuoted(j)
      if (otherwise === null) return fail(`${prefix} has a quote that is never closed.`)
      if (NESTED.test(otherwise[0])) return fail(nestedMessage(prefix))

      elseText = otherwise[0]
      j = skipWhitespace(otherwise[1])

      if (chars[j] === ",") {
        return fail(
          `${prefix} has more than two wordings. It takes the value, the wording when it ` +
            "matches and, optionally, the wording otherwise.",
        )
      }
    }

    if (chars[j] !== ">") return fail(`${prefix} is missing its closing >.`)

    const condition: MessageCondition = {
      index: conditions.length,
      field: name,
      operator,
      value: value[0],
      thenText: thenText[0],
      elseText,
      excerpt,
    }
    conditions.push(condition)
    segments.push(condition)
    return j + 1
  }

  let buffer = ""
  let i = 0

  while (i < chars.length) {
    if (opensConditionAt(i)) {
      if (buffer !== "") segments.push(buffer)
      buffer = ""

      const next = readCondition(i)
      if (next === null) {
        return { source, valid: false, error, conditions: [], segments: [] }
      }
      i = next
    } else {
      buffer += chars[i]
      i += 1
    }
  }

  if (buffer !== "") segments.push(buffer)

  return { source, valid: true, error: null, conditions, segments }
}

function lookupFrom(values: Values): Map<string, string> {
  return new Map(
    Object.entries(values ?? {}).map(([key, value]) => [key.toLowerCase(), String(value ?? "")]),
  )
}

function substitute(text: string, lookup: Map<string, string>): string {
  return text.replace(TOKEN, (_match, name: string) => lookup.get(name.toLowerCase()) ?? "")
}

function placeholders(text: string): string {
  return text.replace(TOKEN, (_match, name: string) => `[${name}]`)
}

export function conditionHolds(condition: MessageCondition, values: Values): boolean {
  const lookup = lookupFrom(values)
  const equal =
    normalizeConditionValue(lookup.get(condition.field.toLowerCase())) ===
    normalizeConditionValue(condition.value)
  return condition.operator === "=" ? equal : !equal
}

/**
 * The member's text. Throws on an invalid template, as the server does — a
 * caller should check `valid` first and show the error instead.
 */
export function renderTemplate(
  template: ParsedMessage,
  values: Values,
  forced: ForcedOutcomes = {},
): string {
  if (!template.valid) throw new Error(template.error ?? "Invalid message")

  const lookup = lookupFrom(values)

  return template.segments
    .map((segment) => {
      if (typeof segment === "string") return substitute(segment, lookup)

      const holds =
        segment.index in forced ? forced[segment.index] : conditionHolds(segment, values)
      return substitute(holds ? segment.thenText : segment.elseText, lookup)
    })
    .join("")
}

/** Token names bracketed; an unpinned condition shows its matching wording. */
export function renderTemplateWithPlaceholders(
  template: ParsedMessage,
  forced: ForcedOutcomes = {},
): string {
  if (!template.valid) throw new Error(template.error ?? "Invalid message")

  return template.segments
    .map((segment) => {
      if (typeof segment === "string") return placeholders(segment)

      const holds = segment.index in forced ? forced[segment.index] : true
      return placeholders(holds ? segment.thenText : segment.elseText)
    })
    .join("")
}

/** One condition's wording on its own, token names bracketed. */
export function wordingWithPlaceholders(text: string): string {
  return placeholders(text)
}

function fixedLength(text: string): number {
  return Array.from(text.replace(TOKEN, "")).length
}

function tokensIn(text: string): string[] {
  return Array.from(text.matchAll(TOKEN), (match) => match[1].toLowerCase())
}

/**
 * The length judged on save against the 300-character limit: the longest
 * version any member could receive, with every {variable} counted as empty.
 * Mirrors MessageTemplate#longest_fixed_length. Each member's own text is
 * measured when it is sent, and one over the limit is not sent.
 */
export function longestFixedLength(template: ParsedMessage): number {
  if (!template.valid) return Array.from(template.source).length

  return template.segments.reduce((total, segment) => {
    if (typeof segment === "string") return total + fixedLength(segment)

    return total + Math.max(fixedLength(segment.thenText), fixedLength(segment.elseText))
  }, 0)
}

/**
 * The {variables} a member's text can be filled in from, lowercased — never a
 * condition's own field. Mirrors MessageTemplate#variables_used.
 */
export function variablesUsed(template: ParsedMessage): string[] {
  if (!template.valid) return []

  const texts = template.segments.flatMap((segment) =>
    typeof segment === "string" ? [segment] : [segment.thenText, segment.elseText],
  )
  return [...new Set(texts.flatMap(tokensIn))]
}

/**
 * One version's length, less its members' details: the fixed characters, and
 * how many times each {variable} is printed. A condition not pinned by
 * `forced` counts its longer wording, so a figure built from this is never
 * short of the truth.
 */
export function versionLength(
  template: ParsedMessage,
  forced: ForcedOutcomes,
): { fixed: number; uses: Record<string, number> } {
  const uses: Record<string, number> = {}
  let fixed = 0

  const add = (text: string) => {
    fixed += fixedLength(text)
    for (const name of tokensIn(text)) uses[name] = (uses[name] ?? 0) + 1
  }

  for (const segment of template.valid ? template.segments : []) {
    if (typeof segment === "string") {
      add(segment)
    } else if (segment.index in forced) {
      add(forced[segment.index] ? segment.thenText : segment.elseText)
    } else {
      add(
        fixedLength(segment.thenText) >= fixedLength(segment.elseText)
          ? segment.thenText
          : segment.elseText,
      )
    }
  }

  return { fixed, uses }
}

/** A condition may only test a column the query returns. */
export function unknownFieldErrors(template: ParsedMessage, variables: string[]): string[] {
  const known = variables.map((name) => name.toLowerCase())
  const seen = new Set<string>()

  return template.conditions
    .filter((condition) => {
      const key = condition.field.toLowerCase()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .filter((condition) => !known.includes(condition.field.toLowerCase()))
    .map(
      (condition) =>
        `The condition starting at ‘${condition.excerpt}’ tests {${condition.field}}, ` +
        "which this automation's query doesn't return.",
    )
}

function quoteWith(text: string, glyph: "'" | '"'): string {
  const family = glyph === "'" ? SINGLE_QUOTES : DOUBLE_QUOTES
  const body = Array.from(text)
    .map((char) => (family.includes(char) ? char + char : char))
    .join("")
  return `${glyph}${body}${glyph}`
}

/** What the Insert condition helper writes. Mirrors MessageTemplate.build. */
export function buildCondition({
  field,
  operator,
  value,
  thenText,
  elseText = "",
}: {
  field: string
  operator: ConditionOperator
  value: string
  thenText: string
  elseText?: string
}): string {
  let out = `<if{${field}}${operator}${quoteWith(value, "'")},${quoteWith(thenText, '"')}`
  if (elseText !== "") out += `,${quoteWith(elseText, '"')}`
  return out + ">"
}
